import { useEffect, useRef, useState } from "react";
import { Bot, Send, Loader2 } from "lucide-react";
import MspShell from "../../components/msp/MspShell";

const SUGGESTIONS = [
  "Plan a trip to Japan",
  "Find the best time to visit Europe",
  "Check visa requirements for USA",
  "Create a packing list",
  "Suggest attractions in Paris",
];

const API = process.env.REACT_APP_BACKEND_URL;
const SID_KEY = "msp_ai_sid";

/* Render Claude's plain-markdown reply as safe React elements
   (bold, links, bullets). Deliberately minimal — no external parser. */
function md(text) {
  const lines = (text || "").split("\n");
  const out = [];
  let list = [];
  const flushList = () => {
    if (list.length) {
      out.push(<ul key={`ul${out.length}`} className="list-disc pl-5 space-y-1 my-1.5">{list}</ul>);
      list = [];
    }
  };
  const inline = (s) => {
    // links [text](url) then **bold**
    const parts = [];
    let rem = s;
    let idx = 0;
    const linkRe = /\[([^\]]+)\]\(([^)]+)\)/;
    while (rem) {
      const m = rem.match(linkRe);
      if (!m) break;
      if (m.index > 0) parts.push(<span key={idx++}>{rem.slice(0, m.index)}</span>);
      parts.push(<a key={idx++} href={m[2]} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline hover:text-blue-500">{m[1]}</a>);
      rem = rem.slice(m.index + m[0].length);
    }
    if (rem) parts.push(<span key={idx++}>{rem}</span>);
    return parts.map((p, i) => {
      if (p.type !== "span" || typeof p.props.children !== "string") return p;
      const t = p.props.children;
      const bold = t.split(/(\*\*[^*]+\*\*)/g).map((seg, j) => seg.startsWith("**") && seg.endsWith("**")
        ? <strong key={j}>{seg.slice(2, -2)}</strong> : <span key={j}>{seg}</span>);
      return <span key={`w${i}`}>{bold}</span>;
    });
  };
  lines.forEach((raw, i) => {
    const l = raw.trimEnd();
    if (/^[-*]\s+/.test(l)) {
      list.push(<li key={i}>{inline(l.replace(/^[-*]\s+/, ""))}</li>);
    } else if (!l) {
      flushList();
      out.push(<div key={i} className="h-1.5" />);
    } else {
      flushList();
      out.push(<p key={i} className="my-1">{inline(l)}</p>);
    }
  });
  flushList();
  return out;
}

export default function MspAssistant() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState([
    { who: "bot", text: "Hi! I'm your **My Social Pulse™ AI Travel Assistant**. Ask me anything — trip ideas, visas, packing, best time to visit, or how to save on flights." },
  ]);
  const scrollRef = useRef(null);
  const sidRef = useRef(typeof window !== "undefined" ? localStorage.getItem(SID_KEY) : null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, busy]);

  const ask = async (text) => {
    const v = (text || q).trim();
    if (!v || busy) return;
    setMsgs((m) => [...m, { who: "me", text: v }]);
    setQ("");
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/msp/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sidRef.current, message: v }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || "AI unavailable");
      if (j.session_id && j.session_id !== sidRef.current) {
        sidRef.current = j.session_id;
        try { localStorage.setItem(SID_KEY, j.session_id); } catch { /* private mode */ }
      }
      setMsgs((m) => [...m, { who: "bot", text: j.reply || "…" }]);
    } catch (e) {
      setMsgs((m) => [...m, { who: "bot", text: `Sorry — I couldn't reach the assistant just now. ${e.message ? `(${e.message})` : ""}` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <MspShell title="AI Travel Assistant" testid="msp-assistant">
      <div ref={scrollRef} data-testid="msp-chat"
        className="bg-white rounded-3xl border border-slate-200 p-5 space-y-3 shadow-sm min-h-[280px] max-h-[52vh] overflow-y-auto">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.who === "me" ? "justify-end" : "items-start gap-2"}`}>
            {m.who === "bot" && (
              <span className="w-9 h-9 shrink-0 rounded-full bg-violet-100 text-violet-700 grid place-items-center"><Bot className="w-5 h-5" /></span>
            )}
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.who === "me" ? "bg-blue-600 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-tl-sm"}`}>
              {m.who === "bot" ? md(m.text) : m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex items-start gap-2" data-testid="msp-chat-thinking">
            <span className="w-9 h-9 shrink-0 rounded-full bg-violet-100 text-violet-700 grid place-items-center"><Bot className="w-5 h-5" /></span>
            <div className="bg-slate-100 rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Thinking…
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="msp-chat-suggestions">
        {SUGGESTIONS.map((s) => (
          <button key={s} onClick={() => ask(s)} disabled={busy}
            data-testid={`msp-chat-chip-${s.toLowerCase().replace(/\s+/g,"-").slice(0,20)}`}
            className="px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-700 hover:bg-blue-100 disabled:opacity-50">
            {s}
          </button>
        ))}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); ask(); }}
        className="flex items-center gap-2 bg-white rounded-2xl border border-slate-200 px-4 py-2 shadow-sm">
        <input value={q} onChange={(e) => setQ(e.target.value)} disabled={busy}
          data-testid="msp-chat-input"
          placeholder="Ask anything travel..."
          className="flex-1 bg-transparent outline-none text-slate-800 disabled:opacity-50" />
        <button data-testid="msp-chat-send" aria-label="Send" disabled={busy || !q.trim()}
          className="w-9 h-9 rounded-full bg-blue-600 text-white grid place-items-center hover:brightness-110 disabled:opacity-40">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </MspShell>
  );
}
